import { DebouncedSearchBar, GlobalLoader, GridIconActions, GridIconButton, PageBreadcrumbsWithLinks, PrimaryPageActions } from '@/components'

import ConfirmationModal from '@/components/ConfirmationModal'

import { KpiStatCard } from '@/components/dashboard'

import { PageBody, PageTitle, PageWrapper } from '@/components/PageWrapper'

import DataGridWithPagination from '@/components/DataGrid/DataGridWithPagination'

import { gridActionsColumnDef, gridHelper, type GridSortingDetails } from '@/helpers/grid.helper'

import { messageHelper } from '@/helpers/message.helper'

import { runWithToast } from '@/helpers/asyncToast.helper'

import { licenseAdminApi, type LicenseAnalyticsSummaryResponse, type LicenseAuditLogResponse } from '@/services/licenseAdminApi'
import { notificationsApi } from '@/services/notificationsApi'

import { LicenseStatusTypes, LicenseTypeTypes, type PaginationResponse, type TenantLicenseListResponse } from '@/services/licenseTypes'
import { multiTenantService } from '@/services/MultiTenantService'
import { subscriptionPlanAdminService } from '@/services/SubscriptionPlanAdminService'
import type { ViewSubscriptionPlanListResponse } from '@/services/subscriptionPlanTypes'

import { AnimationSkeleton } from '@/pages/ui/Skeleton'

import { SearchTenantRequest, type ViewTenantResponse } from '@/helpers/api/WebApiClient'
import { dashboardShellBreadcrumb } from '@/constants/adminPage'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IssueLicenseModal from './Components/IssueLicenseModal'
import ViewLicenseModal from './Components/ViewLicenseModal'
import EditTrialLicenseLimitsModal from './Components/EditTrialLicenseLimitsModal'
import SubscriptionTimelineModal from './Components/SubscriptionTimelineModal'

const ROOT_TENANT_ID = 1

const statusLabel = (s: LicenseStatusTypes) => {
	switch (s) {
		case LicenseStatusTypes.Active:
			return 'Active'
		case LicenseStatusTypes.PendingIssue:
			return 'Pending issue'
		case LicenseStatusTypes.Inactive:
			return 'Inactive'
		case LicenseStatusTypes.Revoked:
			return 'Revoked'
		case LicenseStatusTypes.Expired:
			return 'Expired'
		default:
			return 'Unknown'
	}
}

const statusClass = (s: LicenseStatusTypes) => {
	if (s === LicenseStatusTypes.Active) return 'text-emerald-500'
	if (s === LicenseStatusTypes.PendingIssue) return 'text-amber-500'
	if (s === LicenseStatusTypes.Revoked || s === LicenseStatusTypes.Expired) return 'text-red-500'
	return 'text-[hsl(var(--muted-foreground))]'
}

const typeLabel = (s: LicenseTypeTypes) => (s === LicenseTypeTypes.Trial ? 'Trial' : 'Paid')

const subscriptionStatsLabel = (row: TenantLicenseListResponse) => {
	const current = row.currentSubscriptionCount ?? 0
	const upcoming = row.upcomingSubscriptionCount ?? 0
	const total = row.totalSubscriptionCount ?? current + upcoming

	if (upcoming > 0) return `${total} subscriptions: ${current} current + ${upcoming} upcoming`
	if (current > 0) return `${current} current subscription`
	return 'No active subscription'
}

type ConfirmAction = {
	id: number

	status: LicenseStatusTypes

	title: string

	description: string

	variant: 'primary' | 'destructive' | 'warning'

	reason?: string
}

const SubscriptionSales = () => {
	const { t } = useTranslation()
	const [loading, setLoading] = useState(true)

	const [rowData, setRowData] = useState<PaginationResponse<TenantLicenseListResponse>>()

	const [gridSortingDetails, setGridSortingDetails] = useState<GridSortingDetails>({ sortField: 'createdOn', sortOrder: 'desc' })

	const [filterStatus, setFilterStatus] = useState<LicenseStatusTypes | ''>('')

	const [filterTenantId, setFilterTenantId] = useState<number | ''>('')

	const [filterPlanId, setFilterPlanId] = useState<number | ''>('')

	const [filterType, setFilterType] = useState<LicenseTypeTypes | ''>('')

	const [appliedKeyword, setAppliedKeyword] = useState('')

	const [pageNumber, setPageNumber] = useState(1)

	const [pageSize, setPageSize] = useState(20)

	const [selected, setSelected] = useState<import('@/services/licenseTypes').TenantLicenseDetailResponse | null>(null)

	const [editLimitsTarget, setEditLimitsTarget] = useState<import('@/services/licenseTypes').TenantLicenseDetailResponse | null>(null)

	const [notifications, setNotifications] = useState<{ id: number; title: string; message: string }[]>([])

	const [tenantOptions, setTenantOptions] = useState<ViewTenantResponse[]>([])

	const [planOptions, setPlanOptions] = useState<ViewSubscriptionPlanListResponse[]>([])

	const [tab, setTab] = useState<'licenses' | 'audit'>('licenses')

	const [analytics, setAnalytics] = useState<LicenseAnalyticsSummaryResponse | null>(null)

	const [auditRowData, setAuditRowData] = useState<PaginationResponse<LicenseAuditLogResponse>>()

	const [auditSort, setAuditSort] = useState<GridSortingDetails>({ sortField: 'createdOn', sortOrder: 'desc' })

	const [auditLoading, setAuditLoading] = useState(false)

	const [auditPageNumber, setAuditPageNumber] = useState(1)

	const [auditPageSize, setAuditPageSize] = useState(20)

	const [issueModalOpen, setIssueModalOpen] = useState(false)

	const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)

	const [resendTarget, setResendTarget] = useState<TenantLicenseListResponse | null>(null)

	const [timelineTarget, setTimelineTarget] = useState<TenantLicenseListResponse | null>(null)

	const [statusUpdating, setStatusUpdating] = useState(false)

	const rows = rowData?.data ?? []

	const pendingRows = useMemo(
		() => rows.filter((r) => r.licenseStatus === LicenseStatusTypes.PendingIssue || !r.hasLicenseKey),

		[rows]
	)

	const refreshAnalytics = useCallback(() => {
		licenseAdminApi
			.getAnalyticsSummary()
			.then(setAnalytics)
			.catch(() => setAnalytics(null))
	}, [])

	const loadFilterOptions = useCallback(async () => {
		try {
			const tenantRequest = new SearchTenantRequest()
			tenantRequest.pageNumber = 1
			tenantRequest.pageSize = 500

			const [tenants, plans] = await Promise.all([multiTenantService.getTenant(tenantRequest), subscriptionPlanAdminService.search({ pageNumber: 1, pageSize: 500 })])

			setTenantOptions((tenants.data ?? []).filter((tenant) => tenant.id && tenant.id !== ROOT_TENANT_ID))
			setPlanOptions(plans.data ?? [])
		} catch (e) {
			messageHelper.showError(String(e))
		}
	}, [])

	const loadNotifications = useCallback(async () => {
		try {
			const notesPage = await notificationsApi.list({ pageNumber: 1, pageSize: 5, isRead: false })
			setNotifications(notesPage.data ?? [])
		} catch {
			setNotifications([])
		}
	}, [])

	useEffect(() => {
		refreshAnalytics()
		loadFilterOptions()
		loadNotifications()
	}, [loadFilterOptions, loadNotifications, refreshAnalytics])

	const loadAudit = useCallback(
		async (nextPage = auditPageNumber, nextPageSize = auditPageSize) => {
			setAuditLoading(true)

			try {
				const res = await licenseAdminApi.searchAudit({
					pageNumber: nextPage,
					pageSize: nextPageSize,
					keyword: appliedKeyword || undefined,
					sortField: auditSort.sortField || 'createdOn',
					sortOrder: auditSort.sortOrder || 'desc',
				})

				setAuditRowData(res)
			} catch (e) {
				messageHelper.showError(String(e))
			} finally {
				setAuditLoading(false)
			}
		},
		[appliedKeyword, auditPageNumber, auditPageSize, auditSort]
	)

	useEffect(() => {
		if (tab === 'audit') loadAudit()
	}, [tab, loadAudit])

	const load = useCallback(
		async (nextPage = pageNumber, nextPageSize = pageSize) => {
			setLoading(true)

			try {
				const list = await licenseAdminApi.search({
					pageNumber: nextPage,

					pageSize: nextPageSize,

					tenantId: filterTenantId === '' ? undefined : filterTenantId,

					subscriptionPlanId: filterPlanId === '' ? undefined : filterPlanId,

					licenseStatus: filterStatus === '' ? undefined : filterStatus,

					licenseType: filterType === '' ? undefined : filterType,

					keyword: appliedKeyword || undefined,

					sortField: gridSortingDetails.sortField || 'createdOn',

					sortOrder: gridSortingDetails.sortOrder || 'desc',
				})

				setRowData(list)
			} catch (e) {
				messageHelper.showError(String(e))
			} finally {
				setLoading(false)
			}
		},
		[appliedKeyword, filterPlanId, filterStatus, filterTenantId, filterType, pageNumber, pageSize, gridSortingDetails]
	)

	useEffect(() => {
		load()
	}, [load])

	const handleKeywordChange = useCallback((value: string) => {
		setAppliedKeyword(value.trim())
		setPageNumber(1)
		setAuditPageNumber(1)
	}, [])

	const onGenerate = async (row: TenantLicenseListResponse) => {
		await runWithToast(async () => {
			const detail = await licenseAdminApi.generate(row.tenantSubscriptionPlanId)

			setSelected(detail)

			await load()

			refreshAnalytics()
		})
	}

	const onResend = async (id: number) => {
		await runWithToast(async () => {
			const message = await licenseAdminApi.resendEmail(id)
			messageHelper.showSuccess(message || 'License email sent.')
		})
	}

	const onEditTrialLimits = async (row: TenantLicenseListResponse) => {
		try {
			const detail = await licenseAdminApi.getById(row.id)
			setEditLimitsTarget(detail)
		} catch (e) {
			messageHelper.showError(String(e))
		}
	}

	const applyStatus = async (action: ConfirmAction) => {
		const result = await runWithToast(
			async () => {
				setStatusUpdating(true)
				let responseMessage = ''
				try {
					responseMessage = await licenseAdminApi.updateStatus(action.id, action.status, action.reason)
				} finally {
					setStatusUpdating(false)
				}

				await load()

				refreshAnalytics()

				return responseMessage
			},
			{
				onSuccess: (responseMessage) => {
					messageHelper.showSuccess(responseMessage || 'License status updated.')
				},
			}
		)

		if (!result.ok) {
			throw result.error
		}
	}

	const dismissNotifications = async () => {
		await runWithToast(async () => {
			await notificationsApi.markAllRead()

			setNotifications([])
		})
	}

	const showPending = () => {
		setTab('licenses')

		setFilterStatus(LicenseStatusTypes.PendingIssue)

		setFilterTenantId('')

		setFilterPlanId('')

		setFilterType('')

		setPageNumber(1)
	}

	const clearFilters = () => {
		setFilterStatus('')
		setFilterTenantId('')
		setFilterPlanId('')
		setFilterType('')
		setPageNumber(1)
	}

	const auditColumnDefs = [
		{
			field: 'createdOn',
			headerName: 'When',
			minWidth: 180,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			valueFormatter: (params: { value: string }) => new Date(params.value).toLocaleString(),
		},
		{
			field: 'eventType',
			headerName: 'Event',
			minWidth: 140,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			cellRenderer: (params: { value: string }) => <span className="font-mono text-xs">{params.value}</span>,
		},
		{
			field: 'summary',
			headerName: 'Summary',
			minWidth: 280,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
		},
	] as any

	const licenseColumnDefs = [
		{ field: 'tenantName', headerName: 'Tenant', minWidth: 160, sortingOrder: ['asc', 'desc'], comparator: gridHelper.sortingComparator },
		{
			field: 'adminDisplayName',
			headerName: 'Admin',
			minWidth: 200,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			cellRenderer: (params: { data: TenantLicenseListResponse }) => (
				<div className="flex h-full min-w-0 flex-col justify-center leading-tight">
					<div>{params.data.adminDisplayName}</div>
					<div className="text-xs text-[hsl(var(--muted-foreground))]">{params.data.adminEmail}</div>
				</div>
			),
		},
		{
			field: 'planName',
			headerName: 'Plan',
			minWidth: 180,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			cellRenderer: (params: { data: TenantLicenseListResponse }) => (
				<div className="flex h-full min-w-0 flex-col justify-center leading-tight">
					<div>{params.data.planName}</div>
					<div className="text-xs text-[hsl(var(--muted-foreground))]">{subscriptionStatsLabel(params.data)}</div>
				</div>
			),
		},
		{
			field: 'licenseType',
			headerName: 'Type',
			minWidth: 100,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			valueFormatter: (params: { value: LicenseTypeTypes }) => typeLabel(params.value),
		},
		{
			field: 'licenseStatus',
			headerName: 'Status',
			minWidth: 130,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			cellRenderer: (params: { data: TenantLicenseListResponse }) => (
				<span className={`font-medium ${statusClass(params.data.licenseStatus)}`}>{statusLabel(params.data.licenseStatus)}</span>
			),
		},
		{
			field: 'startedOn',
			headerName: 'Starts',
			minWidth: 120,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			valueFormatter: (params: { value: string }) => new Date(params.value).toLocaleDateString(),
		},
		{
			field: 'expiresOn',
			headerName: 'Expires',
			minWidth: 120,
			sortingOrder: ['asc', 'desc'],
			comparator: gridHelper.sortingComparator,
			valueFormatter: (params: { value: string }) => new Date(params.value).toLocaleDateString(),
		},
		{
			field: 'activeUserCount',
			headerName: 'Users',
			minWidth: 110,
			sortable: false,
			valueFormatter: (params: { data: TenantLicenseListResponse }) => `${params.data.activeUserCount ?? 0} / ${params.data.maxUsers ?? '—'}`,
		},
		{
			field: 'activeInstace',
			headerName: 'Instances',
			minWidth: 110,
			sortable: false,
			valueFormatter: (params: { data: TenantLicenseListResponse }) => `${params.data.activeInstace ?? 0} / ${params.data.maxInstances ?? '—'}`,
		},
		{
			field: 'activeDatabaseCount',
			headerName: 'Databases',
			minWidth: 110,
			sortable: false,
			valueFormatter: (params: { data: TenantLicenseListResponse }) => `${params.data.activeDatabaseCount ?? 0} / ${params.data.maxDatabases ?? '—'}`,
		},
		{
			field: 'id',
			headerName: 'Actions',
			...gridActionsColumnDef,
			minWidth: 240,
			maxWidth: 320,
			cellRenderer: (params: { data: TenantLicenseListResponse }) => {
				const row = params.data
				return (
					<GridIconActions>
						<GridIconButton icon="ri-git-commit-line" title="Subscription timeline" variant="ghost" onClick={() => setTimelineTarget(row)} />
						{(row.licenseStatus === LicenseStatusTypes.PendingIssue || !row.hasLicenseKey) && (
							<GridIconButton icon="ri-key-2-line" title="Generate license" variant="primary" onClick={() => onGenerate(row)} />
						)}
						{row.licenseType === LicenseTypeTypes.Trial && (
							<GridIconButton icon="ri-edit-2-line" title="Edit trial license limits" variant="ghost" onClick={() => onEditTrialLimits(row)} />
						)}
						{row.licenseStatus === LicenseStatusTypes.Active && (
							<>
								<GridIconButton icon="ri-mail-send-line" title="Resend license email" onClick={() => setResendTarget(row)} />
								<GridIconButton
									icon="ri-pause-circle-line"
									title="Stop license (deactivate)"
									variant="warning"
									onClick={() =>
										setConfirmAction({
											id: row.id,
											status: LicenseStatusTypes.Inactive,
											title: 'Deactivate license',
											description: `Stop license for ${row.tenantName}? Desktop activations will be blocked.`,
											variant: 'warning',
										})
									}
								/>
							</>
						)}
						{row.licenseStatus === LicenseStatusTypes.Inactive && (
							<GridIconButton
								icon="ri-play-circle-line"
								title="Reactivate license"
								variant="primary"
								onClick={() =>
									setConfirmAction({
										id: row.id,
										status: LicenseStatusTypes.Active,
										title: 'Reactivate license',
										description: `Restore license for ${row.tenantName}?`,
										variant: 'primary',
									})
								}
							/>
						)}
						{row.licenseStatus !== LicenseStatusTypes.Revoked && (
							<GridIconButton
								icon="ri-forbid-line"
								title="Revoke license"
								variant="danger"
								onClick={() =>
									setConfirmAction({
										id: row.id,
										status: LicenseStatusTypes.Revoked,
										title: 'Revoke license',
										description: `Permanently revoke license for ${row.tenantName}? This cannot be undone.`,
										variant: 'destructive',
										reason: '',
									})
								}
							/>
						)}
						<GridIconButton icon="ri-eye-line" title="View license" variant="ghost" onClick={() => licenseAdminApi.getById(row.id).then(setSelected)} />
					</GridIconActions>
				)
			},
		},
	] as any

	return (
		<>
			<GlobalLoader show={statusUpdating} label="Updating license..." />

			<PageBreadcrumbsWithLinks title={t('Manage.Licenses.Heading', 'License management')} subNames={dashboardShellBreadcrumb(t('Manage.Licenses.Breadcrumb', 'Licenses'))} />

			<PageWrapper className="license-admin">
				<PageTitle
					actions={
						tab === 'licenses' ? (
							<PrimaryPageActions>
								{(analytics?.pendingIssue ?? 0) > 0 && (
									<button type="button" className="btn btn-sm btn-ember-outline" onClick={showPending}>
										<i className="ri-time-line" aria-hidden />
										{analytics!.pendingIssue} pending
									</button>
								)}
								<button type="button" className="btn btn-sm btn-ember" onClick={() => setIssueModalOpen(true)}>
									<i className="ri-key-2-line" aria-hidden />
									Issue license
								</button>
							</PrimaryPageActions>
						) : undefined
					}
				/>
				<PageBody>
					{analytics && (
						<div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
							<KpiStatCard label="Total licenses" value={String(analytics.totalLicenses)} iconClass="ri-file-list-3-line" accentClass="text-primary" />
							<KpiStatCard label="Active licenses" value={String(analytics.activeLicenses)} iconClass="ri-key-2-line" accentClass="text-primary" />
							<KpiStatCard label="Active devices" value={String(analytics.totalActiveDevices)} iconClass="ri-computer-line" accentClass="text-info" />
							<KpiStatCard label="Expiring in 7 days" value={String(analytics.expiringWithin7Days)} iconClass="ri-alarm-warning-line" accentClass="text-warning" />
						</div>
					)}

					<div className="license-admin__tabs mb-4" role="tablist" aria-label="License views">
						<button type="button" role="tab" aria-selected={tab === 'licenses'} className={`license-admin__tab ${tab === 'licenses' ? 'license-admin__tab--active' : ''}`} onClick={() => setTab('licenses')}>
							Licenses
						</button>
						<button type="button" role="tab" aria-selected={tab === 'audit'} className={`license-admin__tab ${tab === 'audit' ? 'license-admin__tab--active' : ''}`} onClick={() => setTab('audit')}>
							Audit log
						</button>
					</div>

					{notifications.length > 0 && (
						<div className="card mb-4 border border-amber-500/30 bg-amber-500/10 p-4">
							<div className="flex flex-wrap items-start justify-between gap-2">
								<h4 className="font-semibold text-amber-600 dark:text-amber-400">
									<i className="ri-notification-3-line me-2" aria-hidden />
									{notifications.length} new purchase notification(s)
								</h4>

								<button type="button" className="license-admin__ghost-btn" onClick={dismissNotifications}>
									Dismiss all
								</button>
							</div>

							<ul className="mt-2 space-y-1 text-sm">
								{notifications.slice(0, 5).map((n) => (
									<li key={n.id}>
										<strong>{n.title}</strong> — {n.message}
									</li>
								))}
							</ul>
						</div>
					)}

					{tab === 'licenses' && (
						<div className="card mb-4 p-4">
							<div className="license-admin__toolbar">
								<select
									className="h-11 w-full max-w-[200px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 text-sm text-[hsl(var(--foreground))] shadow-sm outline-none transition focus:border-[hsl(var(--primary))] focus:ring-4 focus:ring-[hsl(var(--primary))]/10"
									value={filterStatus}
									onChange={(e) => {
										setFilterStatus(e.target.value === '' ? '' : (e.target.value as LicenseStatusTypes))
										setPageNumber(1)
									}}
								>
									<option value="">All statuses</option>

									<option value={LicenseStatusTypes.Active}>Active</option>

									<option value={LicenseStatusTypes.Inactive}>Inactive</option>

									<option value={LicenseStatusTypes.Revoked}>Revoked</option>
								</select>

								<select
									className="h-11 w-full max-w-[200px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 text-sm text-[hsl(var(--foreground))] shadow-sm outline-none transition focus:border-[hsl(var(--primary))] focus:ring-4 focus:ring-[hsl(var(--primary))]/10"
									value={filterTenantId}
									onChange={(e) => {
										setFilterTenantId(e.target.value === '' ? '' : Number(e.target.value))
										setPageNumber(1)
									}}
								>
									<option value="">All tenants</option>

									{tenantOptions.map((tenant) => (
										<option key={tenant.id} value={tenant.id}>
											{tenant.name}
										</option>
									))}
								</select>

								<select
									className="h-11 w-full max-w-[200px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 text-sm text-[hsl(var(--foreground))] shadow-sm outline-none transition focus:border-[hsl(var(--primary))] focus:ring-4 focus:ring-[hsl(var(--primary))]/10"
									value={filterPlanId}
									onChange={(e) => {
										setFilterPlanId(e.target.value === '' ? '' : Number(e.target.value))
										setPageNumber(1)
									}}
								>
									<option value="">All plans</option>

									{planOptions.map((plan) => (
										<option key={plan.id} value={plan.id}>
											{plan.name}
										</option>
									))}
								</select>

								<select
									className="h-11 w-full max-w-[200px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 text-sm text-[hsl(var(--foreground))] shadow-sm outline-none transition focus:border-[hsl(var(--primary))] focus:ring-4 focus:ring-[hsl(var(--primary))]/10"
									value={filterType}
									onChange={(e) => {
										setFilterType(e.target.value === '' ? '' : (e.target.value as LicenseTypeTypes))
										setPageNumber(1)
									}}
								>
									<option value="">All types</option>

									<option value={LicenseTypeTypes.Trial}>Trial</option>

									<option value={LicenseTypeTypes.Paid}>Paid</option>
								</select>

								<DebouncedSearchBar placeholder="Search tenant, admin, plan…" value={appliedKeyword} onChange={handleKeywordChange} minSearchLength={3} name="licenseSearch" id="licenseSearch" />

								{(filterStatus || filterTenantId || filterPlanId || filterType) && (
									<button type="button" className="btn btn-sm btn-ember-outline h-11" onClick={clearFilters}>
										Clear filters
									</button>
								)}
							</div>
						</div>
					)}

					{tab === 'audit' ? (
						auditLoading && !auditRowData ? (
							<AnimationSkeleton />
						) : auditRowData ? (
							<DataGridWithPagination
								rowData={auditRowData}
								columnDefs={auditColumnDefs}
								onSortChanged={(event) => {
									setAuditPageNumber(1)
									setAuditSort(gridHelper.handleSortChanged(event))
								}}
								onPageChange={(page, pageSize) => {
									const nextPageSize = pageSize ?? auditPageSize
									setAuditPageSize(nextPageSize)
									setAuditPageNumber(page)
								}}
							/>
						) : (
							<div className="p-8 text-center text-[hsl(var(--muted-foreground))]">No audit entries.</div>
						)
					) : loading && !rowData ? (
						<AnimationSkeleton />
					) : rows.length === 0 ? (
						<div className="p-10 text-center">
							<p className="text-[hsl(var(--muted-foreground))]">No licenses found.</p>
							<p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
								Assign a plan and issue a license for a customer, or generate pending purchases after Stripe checkout.
							</p>
							<button type="button" className="btn btn-ember license-admin__empty-cta" onClick={() => setIssueModalOpen(true)}>
								<i className="ri-key-2-line" aria-hidden />
								Issue license
							</button>
						</div>
					) : (
						<DataGridWithPagination
							rowData={rowData}
							columnDefs={licenseColumnDefs}
							onSortChanged={(event) => {
								setPageNumber(1)
								setGridSortingDetails(gridHelper.handleSortChanged(event))
							}}
							onPageChange={(page, nextSize) => {
								const nextPageSize = nextSize ?? pageSize
								setPageSize(nextPageSize)
								setPageNumber(page)
							}}
							rowHeight={56}
						/>
					)}
				</PageBody>
			</PageWrapper>

			<IssueLicenseModal
				isOpen={issueModalOpen}
				onClose={() => setIssueModalOpen(false)}
				onComplete={() => {
					load()
					refreshAnalytics()
				}}
				pendingRows={pendingRows}
			/>

			<ViewLicenseModal license={selected} onClose={() => setSelected(null)} />

			<SubscriptionTimelineModal license={timelineTarget} onClose={() => setTimelineTarget(null)} />

			<EditTrialLicenseLimitsModal
				license={editLimitsTarget}
				onClose={() => setEditLimitsTarget(null)}
				onSaved={(updated) => {
					setEditLimitsTarget(null)
					setSelected((current) => (current?.id === updated.id ? updated : current))
					load()
					refreshAnalytics()
				}}
			/>

			{resendTarget && <ConfirmationModal isOpen={!!resendTarget} onClose={() => setResendTarget(null)} onConfirm={() => onResend(resendTarget.id)} title="Resend license email" description={`Resend the license email to ${resendTarget.adminEmail}?`} confirmButtonText="Resend email" variant="primary" />}

			{confirmAction && (
				<ConfirmationModal
					isOpen={!!confirmAction}
					onClose={() => setConfirmAction(null)}
					onConfirm={(reason) => {
						if (confirmAction) applyStatus({ ...confirmAction, reason })
					}}
					title={confirmAction.title}
					description={confirmAction.description}
					confirmButtonText={confirmAction.title}
					variant={confirmAction.variant}
					requireReason={confirmAction.status === LicenseStatusTypes.Revoked}
					reasonLabel="Revocation reason"
					reasonPlaceholder="Explain why this license is being revoked…"
					initialReason={confirmAction.reason}
				/>
			)}
		</>
	)
}

export default SubscriptionSales
